import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { 
  BookOpen, CheckCircle, ArrowRight, Loader, Quote, RefreshCw,
  BookText, Pencil
} from "lucide-react";
import { AvatarDisplay } from "./parent";
import { BottomNav } from "./ChildDashboard";
import { MascotImage } from "./Mascots";
import { useLanguage } from "../hooks/useLanguage";
import DrawingCanvas from "./DrawingCanvas";
import AnswerBox from "./AnswerBox";
import { ShareButton } from "./SocialShare";
import { WordSearchSection, QuizSection } from "./lesson";
import AudioReader from "./AudioReader";

export default function LessonPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [child, setChild] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [error, setError] = useState("");
  const [lessonResponses, setLessonResponses] = useState(null);

  useEffect(() => { loadLesson(); }, [childId]);

  const loadLesson = async () => {
    setLoading(true); setError("");
    try {
      const [cr, lr] = await Promise.all([
        axios.get(`${API}/children/${childId}`),
        axios.get(`${API}/lessons/today/${childId}`),
      ]);
      setChild(cr.data); 
      setLesson(lr.data);
      
      try {
        const resp = await axios.get(`${API}/lessons/${lr.data.id}/responses/${childId}`);
        setLessonResponses(resp.data);
      } catch (e) { /* No responses yet */ }
    } catch (e) { setError(e.response?.data?.detail || t('could_not_load')); }
    finally { setLoading(false); }
  };

  const handleComplete = async () => {
    if (lesson?.completed || completing) return;
    setCompleting(true);
    try {
      const { data } = await axios.post(`${API}/lessons/${lesson.id}/complete`);
      setLesson(data); setJustCompleted(true);
    } catch (e) { console.error(e); }
    finally { setCompleting(false); }
  };

  const saveDrawing = async (dataUrl, activityType) => {
    try {
      await axios.post(`${API}/lessons/${lesson.id}/drawing`, {
        child_id: childId, drawing_data: dataUrl, activity_type: activityType
      });
    } catch (e) { console.error("Failed to save drawing:", e); }
  };

  const saveAnswer = async (content, promptIndex) => {
    try {
      await axios.post(`${API}/lessons/${lesson.id}/answer`, {
        child_id: childId, content: content, prompt_index: promptIndex
      });
    } catch (e) { console.error("Failed to save answer:", e); }
  };

  // Loading State
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pb-20" style={{ background: "linear-gradient(180deg, #FFD166 0%, #F77F00 100%)" }}>
      <div className="text-center px-4">
        <div className="relative">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-white/30 flex items-center justify-center animate-pulse">
            <BookOpen size={48} color="white" className="wiggle-slow" />
          </div>
          <div className="absolute -top-2 -left-2 text-3xl float" style={{ animationDelay: '0s' }}>⭐</div>
          <div className="absolute -top-2 -right-2 text-2xl float" style={{ animationDelay: '0.5s' }}>✨</div>
          <div className="absolute -bottom-2 left-4 text-2xl float" style={{ animationDelay: '1s' }}>🌟</div>
        </div>
        <p className="font-black text-2xl text-white drop-shadow-lg" style={{ fontFamily: "'Baloo 2', cursive" }}>
          {t('preparing_lesson')}
        </p>
        <p className="text-white/80 text-base mt-2 font-bold" style={{ fontFamily: "'Comic Neue', cursive" }}>
          {t('creating_special')}
        </p>
      </div>
    </div>
  );

  // Error State
  if (error) return (
    <div className="min-h-screen flex items-center justify-center pb-20 px-4" style={{ background: "#F0F4FF" }}>
      <div className="text-center max-w-sm bg-white rounded-3xl p-8 shadow-lg">
        <p className="text-red-500 font-bold mb-4">{error}</p>
        <button onClick={loadLesson}
          className="flex items-center gap-2 mx-auto text-white font-bold px-6 py-3 rounded-xl"
          style={{ background: "#118AB2" }} data-testid="retry-lesson-btn">
          <RefreshCw size={16} /> {t('retry')}
        </button>
      </div>
      <BottomNav childId={childId} active="lesson" />
    </div>
  );

  const isOlderKid = child?.age_group === "big" || child?.age_group === "teen";
  const isLittleKid = child?.age_group === "little";

  const bgGradient = isLittleKid 
    ? "linear-gradient(180deg, #FFE5EC 0%, #FFF0F5 100%)"
    : isOlderKid 
      ? "linear-gradient(180deg, #E8F4FD 0%, #F0F4FF 100%)"
      : "linear-gradient(180deg, #E8FFF5 0%, #F0F4FF 100%)";

  return (
    <div className="min-h-screen pb-24 relative overflow-hidden" style={{ background: bgGradient }}>
      {/* Decorative floating elements for little kids */}
      {isLittleKid && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
          <div className="absolute top-20 left-4 text-4xl float opacity-30" style={{ animationDelay: '0s' }}>☁️</div>
          <div className="absolute top-40 right-8 text-3xl float opacity-30" style={{ animationDelay: '1s' }}>⭐</div>
          <div className="absolute top-60 left-1/4 text-2xl float opacity-20" style={{ animationDelay: '2s' }}>🌈</div>
          <div className="absolute bottom-40 right-4 text-3xl float opacity-30" style={{ animationDelay: '0.5s' }}>☁️</div>
          <div className="absolute bottom-60 left-8 text-2xl float opacity-20" style={{ animationDelay: '1.5s' }}>✨</div>
        </div>
      )}
      
      {/* Header */}
      <LessonHeader child={child} isLittleKid={isLittleKid} t={t} />

      <div className="max-w-lg mx-auto px-4 -mt-4 relative z-10">
        {/* Lesson Illustration + Title in a row on larger screens */}
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          {/* Lesson Illustration */}
          {lesson?.illustration_url && (
            <div className="rounded-[28px] overflow-hidden shadow-xl border-4 border-white">
              <img src={lesson.illustration_url} alt="Lesson illustration"
                className="w-full h-48 md:h-full object-cover" data-testid="lesson-illustration" />
            </div>
          )}

          {/* Lesson Title Card */}
          <div className={lesson?.illustration_url ? "" : "md:col-span-2"}>
            <LessonTitleCard lesson={lesson} isLittleKid={isLittleKid} t={t} />
            {/* Bible Verse inline - hide when lesson is completed */}
            {!(lesson?.completed || justCompleted) && (
              <BibleVerseCard lesson={lesson} isLittleKid={isLittleKid} />
            )}
            {/* Audio Read-Aloud for ALL kids */}
            {lesson && !(lesson?.completed || justCompleted) && (
              <div className="mt-3">
                <AudioReader
                  sections={[
                    { title: "Today's Lesson", text: lesson?.title || '' },
                    lesson?.bible_verse ? { title: "Bible Verse", text: lesson.bible_verse } : null,
                    lesson?.bible_story ? { title: "Bible Story", text: lesson.bible_story } : null,
                    lesson?.lesson_explanation ? { title: "What We Learn", text: lesson.lesson_explanation } : null,
                    lesson?.moral ? { title: "Remember This", text: lesson.moral } : null,
                  ].filter(Boolean)}
                  label="Listen to the lesson"
                />
              </div>
            )}
          </div>
        </div>

        {/* Story and Explanation side by side - hide when lesson is completed */}
        {!(lesson?.completed || justCompleted) && (
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Bible Story */}
            {lesson?.bible_story && (
              <ContentCard title={isLittleKid ? "Story Time! 📖" : "The Story"} 
                content={lesson.bible_story} borderColor="#FFD166" emoji="📚" isLittleKid={isLittleKid} testId="bible-story" stepNumber={1} />
            )}

            {/* Explanation */}
            <ContentCard title={isLittleKid ? "What Does It Mean? 🤔" : t('what_this_means')} 
              content={lesson?.lesson_explanation} borderColor="#06D6A0" emoji="💡" isLittleKid={isLittleKid} testId="lesson-explanation" stepNumber={2} />
          </div>
        )}

        {/* Real Life Example and Moral side by side - hide when completed */}
        {!(lesson?.completed || justCompleted) && (
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Real Life Example */}
            {lesson?.real_life_example && (
              <ContentCard title={isLittleKid ? "In Real Life! 🏠" : t('real_life_example')} 
                content={lesson.real_life_example} borderColor="#118AB2" emoji="🌟" isLittleKid={isLittleKid} testId="real-life-example" stepNumber={3} />
            )}

            {/* Moral */}
            <MoralCard lesson={lesson} isLittleKid={isLittleKid} t={t} stepNumber={lesson?.real_life_example ? 4 : 3} />
          </div>
        )}

        {/* Fun Activity Hint */}
        {lesson?.fun_activity_hint && (
          <FunActivityCard hint={lesson.fun_activity_hint} isLittleKid={isLittleKid} t={t} />
        )}

        {/* Drawing Area */}
        {lesson?.fun_activity_hint && (
          <DrawingCanvas 
            title={isLittleKid ? "🎨 Draw What You Learned!" : "✏️ Draw Your Fun Idea!"}
            height={isLittleKid ? 280 : 220}
            onSave={saveDrawing}
            savedDrawing={lessonResponses?.drawings?.find(d => d.activity_type === "fun_idea")?.drawing_data}
            lessonId={lesson?.id}
            childId={childId}
            activityType="fun_idea"
          />
        )}

        {/* Word Search - Older Kids */}
        {isOlderKid && lesson?.word_search_words && lesson.word_search_words.length > 0 && (
          <WordSearchSection words={lesson.word_search_words} t={t} />
        )}

        {/* Answer Boxes - Older Kids - 2 columns on larger screens */}
        {isOlderKid && lesson?.reflection_prompts && lesson.reflection_prompts.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <Pencil size={18} color="#9B5DE5" strokeWidth={2.5} />
              <h3 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('think_and_write') || 'Think & Write'}
              </h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {lesson.reflection_prompts.map((prompt, i) => (
                <AnswerBox key={i} prompt={prompt} placeholder={t('write_thoughts') || "Write your thoughts here..."}
                  onSave={saveAnswer} savedAnswer={lessonResponses?.answers?.find(a => a.prompt_index === i)?.content}
                  lessonId={lesson?.id} childId={childId} promptIndex={i} />
              ))}
            </div>
          </div>
        )}

        {/* Quiz */}
        {lesson?.quiz_questions && lesson.quiz_questions.length > 0 && (
          <QuizSection questions={lesson.quiz_questions} ageGroup={child?.age_group} lessonId={lesson?.id} t={t} />
        )}

        {/* Complete / Done Section */}
        {lesson?.completed || justCompleted ? (
          <CompletionSection 
            child={child} lesson={lesson} lessonResponses={lessonResponses}
            isLittleKid={isLittleKid} navigate={navigate} childId={childId} t={t} />
        ) : (
          <button onClick={handleComplete} disabled={completing}
            className="w-full text-white font-black py-5 rounded-[20px] border-b-4 active:border-b-0 active:translate-y-1 transition-all text-xl disabled:opacity-60 mb-4 relative overflow-hidden"
            style={{ fontFamily: "'Baloo 2', cursive", background: "linear-gradient(90deg, #06D6A0, #118AB2)", borderColor: "#049a73" }}
            data-testid="complete-lesson-btn">
            <span className="relative z-10 flex items-center justify-center gap-2">
              {completing ? (
                <><Loader size={24} className="animate-spin" /> {t('loading')}</>
              ) : (
                <><CheckCircle size={24} /> {isLittleKid ? "I Learned This! ✅" : t('i_learned_this')}</>
              )}
            </span>
          </button>
        )}
      </div>
      <BottomNav childId={childId} active="lesson" />
    </div>
  );
}

// Sub-components
function LessonHeader({ child, isLittleKid, t }) {
  return (
    <div className="px-4 pt-6 pb-10 relative"
      style={{ background: isLittleKid 
        ? "linear-gradient(135deg, #EF476F 0%, #FFD166 100%)" 
        : "linear-gradient(135deg, #073B4C 0%, #118AB2 100%)" }}>
      <div className="absolute top-2 right-2 w-16 h-16 rounded-full bg-white/10"></div>
      <div className="absolute bottom-4 left-4 w-8 h-8 rounded-full bg-white/10"></div>
      
      <div className="max-w-lg mx-auto flex items-center gap-3 mb-4 relative z-10">
        {child && (
          <div className="kid-bounce">
            <AvatarDisplay avatar={child.avatar} size={52} />
          </div>
        )}
        <div>
          <p className="text-white/70 text-xs font-bold uppercase tracking-wider">{t('todays_lesson_for')}</p>
          <h1 className="text-3xl font-black text-white drop-shadow-md" style={{ fontFamily: "'Baloo 2', cursive" }}>
            {child?.name}
          </h1>
        </div>
      </div>
    </div>
  );
}

function LessonTitleCard({ lesson, isLittleKid, t }) {
  return (
    <div className="bg-white rounded-[24px] p-5 shadow-lg mb-4 border-b-4 relative overflow-hidden" 
      style={{ borderColor: isLittleKid ? "#EF476F" : "#118AB2" }}>
      <div className="absolute -top-4 -right-4 w-14 h-14 rounded-full opacity-20"
        style={{ background: isLittleKid ? "#EF476F" : "#118AB2" }}></div>
      
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: isLittleKid ? "#FFF0F3" : "#E0F4FF" }}>
          <BookOpen size={20} color={isLittleKid ? "#EF476F" : "#118AB2"} strokeWidth={2.5} />
        </div>
        <span className="text-xs font-black uppercase tracking-wider"
          style={{ color: isLittleKid ? "#EF476F" : "#118AB2", fontFamily: "'Baloo 2', cursive" }}>
          {t('todays_lesson')}
        </span>
      </div>
      <h2 className="text-xl font-black text-[#073B4C] leading-tight" 
        style={{ fontFamily: "'Baloo 2', cursive" }} data-testid="lesson-title">
        {lesson?.lesson_title}
      </h2>
    </div>
  );
}

function BibleVerseCard({ lesson, isLittleKid }) {
  return (
    <div className="rounded-[24px] p-5 shadow-xl relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #9B5DE5 0%, #118AB2 100%)" }}>
      <div className="absolute top-2 right-3 text-xl opacity-50 sparkle">✨</div>
      
      <Quote size={24} className="mb-2 opacity-60" color="white" />
      <p className="text-white text-lg font-bold leading-relaxed mb-3"
        style={{ fontFamily: isLittleKid ? "'Comic Neue', cursive" : "'Quicksand', sans-serif" }} 
        data-testid="bible-verse">
        "{lesson?.bible_verse}"
      </p>
      <span className="inline-flex items-center gap-2 bg-white/25 text-white text-xs font-black px-3 py-1.5 rounded-full"
        data-testid="verse-reference">
        📖 {lesson?.verse_reference}
      </span>
    </div>
  );
}

function ContentCard({ title, content, borderColor, emoji, isLittleKid, testId, stepNumber }) {
  if (!content) return null;
  return (
    <div className="bg-white rounded-[24px] p-5 shadow-lg border-l-6 relative h-full" style={{ borderColor, borderLeftWidth: '6px' }}>
      {/* Step number badge - hidden on mobile, shown on md+ */}
      {stepNumber && (
        <div className="hidden md:flex absolute -top-3 -left-3 w-8 h-8 rounded-full items-center justify-center text-white font-black text-sm shadow-lg"
          style={{ background: borderColor, fontFamily: "'Baloo 2', cursive" }}>
          {stepNumber}
        </div>
      )}
      <div className="absolute -top-1 -right-1 text-2xl">{emoji}</div>
      <div className="flex items-center gap-2 mb-2">
        <BookText size={20} color={borderColor} strokeWidth={2.5} />
        <h3 className="font-black text-[#073B4C] text-base" style={{ fontFamily: "'Baloo 2', cursive" }}>{title}</h3>
      </div>
      <p className="text-gray-700 leading-relaxed text-sm" 
        style={{ fontFamily: isLittleKid ? "'Comic Neue', cursive" : "'Quicksand', sans-serif" }}
        data-testid={testId}>
        {content}
      </p>
    </div>
  );
}

function MoralCard({ lesson, isLittleKid, t, stepNumber }) {
  return (
    <div className="relative h-full">
      {/* Step number badge - hidden on mobile, shown on md+ */}
      {stepNumber && (
        <div className="hidden md:flex absolute -top-3 -left-3 w-8 h-8 rounded-full items-center justify-center text-[#F77F00] font-black text-sm shadow-lg bg-white z-10"
          style={{ fontFamily: "'Baloo 2', cursive" }}>
          {stepNumber}
        </div>
      )}
      <div className="rounded-[24px] p-5 border-b-4 relative overflow-hidden h-full"
        style={{ background: "linear-gradient(135deg, #FFD166, #F77F00)", borderColor: "#e0a030" }}>
        <div className="absolute -top-1 -right-1 text-3xl opacity-30">🎯</div>
        <p className="text-xs font-black text-white/90 uppercase tracking-wider mb-2" 
          style={{ fontFamily: "'Baloo 2', cursive" }}>
          {isLittleKid ? "Remember This! ☝️" : t('todays_moral')}
        </p>
        <p className="font-black text-white text-base leading-relaxed" 
          style={{ fontFamily: isLittleKid ? "'Comic Neue', cursive" : "'Quicksand', sans-serif" }}
          data-testid="lesson-moral">
          {lesson?.moral}
        </p>
      </div>
    </div>
  );
}

function FunActivityCard({ hint, isLittleKid, t }) {
  return (
    <div className="rounded-[24px] p-5 mb-4 border-b-4 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #EF476F, #9B5DE5)", borderColor: "#c73860" }}>
      <div className="absolute -top-1 -right-1 text-3xl opacity-40 wiggle-slow">🎨</div>
      <p className="text-xs font-black text-white/90 uppercase tracking-wider mb-2"
        style={{ fontFamily: "'Baloo 2', cursive" }}>
        {isLittleKid ? "Fun Time! 🎉" : t('fun_idea')}
      </p>
      <p className="text-white font-bold text-sm leading-relaxed" 
        style={{ fontFamily: isLittleKid ? "'Comic Neue', cursive" : "'Quicksand', sans-serif" }}>
        {hint}
      </p>
    </div>
  );
}

function CompletionSection({ child, lesson, lessonResponses, isLittleKid, navigate, childId, t }) {
  return (
    <div>
      <div className="rounded-[28px] p-8 mb-5 text-center border-4 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #E8FFF5, #D0F4EA)", borderColor: "#06D6A0" }}>
        <div className="absolute top-2 left-4 text-3xl sparkle">🎉</div>
        <div className="absolute top-4 right-4 text-2xl sparkle" style={{ animationDelay: '0.3s' }}>⭐</div>
        <div className="absolute bottom-4 left-6 text-2xl sparkle" style={{ animationDelay: '0.6s' }}>✨</div>
        <div className="absolute bottom-2 right-8 text-3xl sparkle" style={{ animationDelay: '0.9s' }}>🌟</div>
        
        <MascotImage mascot="lion" size={80} className="absolute -right-2 -top-2 opacity-20" />
        <div className="w-20 h-20 rounded-full bg-[#06D6A0] mx-auto mb-4 flex items-center justify-center pop-loop">
          <CheckCircle size={48} color="white" strokeWidth={2.5} />
        </div>
        <p className="font-black text-3xl text-[#073B4C] mb-2" style={{ fontFamily: "'Baloo 2', cursive" }}>
          {isLittleKid ? "YAY! Great Job! 🎊" : t('amazing_job')}
        </p>
        <p className="text-gray-600 text-base font-semibold" style={{ fontFamily: "'Comic Neue', cursive" }}>
          {t('lesson_completed')}
        </p>
        
        <div className="mt-5">
          <ShareButton
            type="lessonComplete"
            data={{
              childName: child?.name,
              lessonTitle: lesson?.lesson_title,
              verseReference: lesson?.verse_reference,
              moral: lesson?.moral,
              drawingData: lessonResponses?.drawings?.find(d => d.activity_type === "fun_idea")?.drawing_data,
              hasDrawing: lessonResponses?.drawings?.length > 0,
              quizScore: lessonResponses?.quiz_answers ? 
                `${Object.values(lessonResponses.quiz_answers).filter((a, i) => 
                  a === lesson?.quiz_questions?.[i]?.correct_index
                ).length}/${lesson?.quiz_questions?.length || 0}` : null,
              reflectionAnswer: lessonResponses?.answers?.[0]?.content,
            }}
            className="bg-gradient-to-r from-[#EF476F] to-[#9B5DE5] text-white hover:opacity-90 font-black text-lg px-8 py-4 shadow-lg"
            buttonText={isLittleKid ? "Tell Everyone! 📣" : "🎉 Share My Achievement!"}
            size="large"
          />
        </div>
      </div>
      <button onClick={() => navigate(`/child/${childId}/tasks`)}
        className="w-full text-white font-black py-5 rounded-[20px] border-b-4 active:border-b-0 active:translate-y-1 transition-all flex items-center justify-center gap-3 text-xl"
        style={{ fontFamily: "'Baloo 2', cursive", background: "linear-gradient(90deg, #9B5DE5, #EF476F)", borderColor: "#7a3fc3" }}
        data-testid="go-to-tasks-btn">
        {isLittleKid ? "Let's Do Activities! 🎮" : t('now_do_activities')} <ArrowRight size={26} strokeWidth={2.5} />
      </button>
    </div>
  );
}
